import React, { useState, useEffect, memo, useLayoutEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import PropTypes from 'prop-types';
import { scrollToTop, upLoadFilePromise } from '../../utils/smartutils';
import SelectSmartHtmlEditor from './SelectSmartHtmlEditor';
import { EMPTY_STRING } from '../../utils/constants';

const SmartHtmlEditor = (props) => {
  const {
    onEditorChange,
    htmlValue,
    meta,
    shouldClear,
    readOnly,
    onTemplateSelect,
    showTemplateCommunication,
    foreignContent,
    scrollError,
  } = props;
  const [editorState, setEditorState] = useState();

  const onEditorStateChange = (edtState) => {
    setEditorState(edtState);
  };

  const onChange = (value) => {
    const { blocks } = value;
    const textValue = blocks
      .map((block) => (!block.text.trim() && '\n') || block.text)
      .join('\n');
    const hasValue = textValue.replaceAll('\n', EMPTY_STRING).length;
    onEditorChange(
      hasValue ? draftToHtml(value) : EMPTY_STRING,
      hasValue ? textValue : EMPTY_STRING
    );
  };

  const ToolbarOptions = {
    options: [
      'inline',
      'colorPicker',
      'blockType',
      'list',
      'textAlign',
      'link',
      'emoji',
      'history',
      'remove',
    ],
    inline: {
      options: ['bold', 'italic', 'underline'],
    },
    blockType: {
      inDropdown: true,
      options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'],
      className: undefined,
      component: undefined,
      dropdownClassName: undefined,
    },
    image: {
      className: undefined,
      component: undefined,
      popupClassName: undefined,
      urlEnabled: true,
      uploadEnabled: true,
      alignmentEnabled: true,
      uploadCallback: upLoadFilePromise,
      previewImage: true,
      inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg',
      alt: { present: false, mandatory: false },
      defaultSize: {
        height: 'auto',
        width: 'auto',
      },
    },
  };

  const toolbarCustomButtons = [];

  if (showTemplateCommunication) {
    toolbarCustomButtons.push(
      <SelectSmartHtmlEditor onTemplateChange={onTemplateSelect} />
    );
  }

  useLayoutEffect(() => {
    if (foreignContent) {
      setEditorState(
        EditorState.createWithContent(
          ContentState.createFromBlockArray(htmlToDraft(foreignContent))
        )
      );
    }
  }, [foreignContent]);

  useEffect(() => {
    if (shouldClear) {
      setEditorState(EditorState.createEmpty());
    }
  }, [shouldClear]);

  useEffect(() => {
    if (htmlValue) {
      setEditorState(
        EditorState.createWithContent(
          ContentState.createFromBlockArray(htmlToDraft(htmlValue))
        )
      );
    } else {
      setEditorState(EditorState.createEmpty());
    }
  }, []);

  return (
    <div className="form__form-group-input-wrap form__form-group-input-wrap--error-above">
      <div className="text-editor">
        <Editor
          editorState={editorState}
          wrapperClassName="demo-wrapper"
          editorClassName="demo-editor"
          onEditorStateChange={onEditorStateChange}
          onChange={onChange}
          toolbar={ToolbarOptions}
          toolbarCustomButtons={toolbarCustomButtons}
          handlePastedText={() => false}
          localization={{
            locale: 'pt',
          }}
          readOnly={readOnly}
        />
      </div>
      {meta.touched && meta.error && (
        <span className="form__form-group-error">
          {meta.error} {scrollError && scrollToTop()}
        </span>
      )}
    </div>
  );
};

SmartHtmlEditor.propTypes = {
  onEditorChange: PropTypes.func.isRequired,
  htmlValue: PropTypes.string,
  shouldClear: PropTypes.bool,
  scrollError: PropTypes.bool,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
  readOnly: PropTypes.bool,
  onTemplateSelect: PropTypes.func,
  showTemplateCommunication: PropTypes.bool,
  foreignContent: PropTypes.string,
};

SmartHtmlEditor.defaultProps = {
  htmlValue: '',
  meta: null,
  scrollError: true,
  shouldClear: false,
  readOnly: false,
  onTemplateSelect: null,
  showTemplateCommunication: true,
  foreignContent: '',
};

export default withTranslation('common')(memo(SmartHtmlEditor));
